import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Minus, Plus, X } from 'lucide-react';
import type { StorePublicProduct } from '@evi/shared';
import { countChargerUnits, computeStoreFreight, BULK_QUOTE_MIN_CHARGER_UNITS } from '@evi/shared';
import { cartItemImage } from '@evi/store-kit';
import { useCatalog } from '@/hooks/useCatalog';
import { useCart } from '@/lib/cartStore';
import { formatUsd, startGuestCheckout, type GuestCheckoutItem } from '@/lib/api';
import styles from './CartPage.module.css';

async function readApiError(e: unknown): Promise<{ code?: string; message?: string } | null> {
  const resp = (e as { response?: Response }).response;
  if (!resp) return null;
  try {
    const j = (await resp.json()) as { error?: { code?: string; message?: string } };
    return j.error ?? null;
  } catch {
    return null;
  }
}

export function CartPage() {
  const locationId = useCart(s => s.installLocationId);
  const [draftMessage, setDraftMessage] = useState('');
  const [draftItems] = useState<Array<{ product_id: string; quantity: number }>>(() => {
    try { const v: unknown = JSON.parse(new URLSearchParams(window.location.search).get('items') ?? '[]');
      if (!Array.isArray(v) || v.length > 50 || !v.every(x => x && /^[a-f0-9]{24}$/i.test(x.product_id) && Number.isInteger(x.quantity) && x.quantity > 0 && x.quantity <= 99)) return [];
      return v;
    } catch { return []; }
  });
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('install_location_id');
    if (id && /^[a-f0-9]{24}$/i.test(id)) useCart.getState().setInstallLocation(id);
  }, []);
  const draft = draftItems.length ? <aside><p>A draft cart is ready for your review. Only you place and pay for an order.</p><button className="eviBtn eviBtnPrimary" onClick={() => { for (const item of draftItems) useCart.getState().setQty(item.product_id, 0); for (const item of draftItems) useCart.getState().add(item.product_id, item.quantity); setDraftMessage('Draft items added. Review quantities before checkout.'); }}>Add draft items</button><p role="status">{draftMessage}</p></aside> : null;

  const lines = useCart((s) => s.lines);
  const setQty = useCart((s) => s.setQty);
  const remove = useCart((s) => s.remove);
  const { data: catalog } = useCatalog();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rows: Array<{ productId: string; quantity: number; product: StorePublicProduct }> = [];
  for (const l of lines) {
    const product = catalog?.find((p) => p.id === l.productId);
    if (product) rows.push({ productId: l.productId, quantity: l.quantity, product });
  }

  const freightItems = rows.map((r) => ({
    part_number: r.product.part_number,
    name: r.product.name,
    tier: r.product.tier,
    quantity: r.quantity,
  }));
  const chargerUnits = countChargerUnits(freightItems);
  const bulk = chargerUnits >= BULK_QUOTE_MIN_CHARGER_UNITS;
  const subtotalCents = rows.reduce((s, r) => s + r.product.price_cents * r.quantity, 0);
  const freight = computeStoreFreight(freightItems);
  // Freight priced live (Warren, 21 October 2026, ruling B): with a US ZIP, the checkout shows it as one line,
  // "Hardware shipped to location"; without one, the per-unit estimate stands.
  const [zip, setZip] = useState('');
  const shipZip = /^\d{5}$/.test(zip.trim()) ? zip.trim() : undefined;

  const onCheckout = async () => {
    setBusy(true);
    setError(null);
    try {
      const items: GuestCheckoutItem[] = rows.map((r) => ({
        product_id: r.productId,
        quantity: r.quantity,
      }));
      const { url } = await startGuestCheckout(items, locationId, shipZip);
      window.location.href = url;
    } catch (e) {
      const body = await readApiError(e);
      setError(body?.message ?? 'Checkout could not start. Please try again.');
      setBusy(false);
    }
  };

  if (rows.length === 0) {
    return (
      <div className={styles.empty}>
        <h1 className={styles.emptyTitle}>Your cart is empty</h1>{draft}
        <Link to="/" className="eviBtn eviBtnGhost">
          Browse the store
        </Link>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Cart</h1>{draft}
      {locationId && <p>This order is linked to your installation. Confirm its delivery address at checkout.</p>}
      <div className={styles.layout}>
        <ul className={styles.rows}>
          {rows.map((r) => {
            const img = catalog ? cartItemImage(r.productId, catalog) : null;
            return (
              <li key={r.productId} className={styles.row}>
                <div className={styles.thumb}>
                  {img ? <img src={img} alt="" loading="lazy" /> : <div className={styles.thumbFallback} />}
                </div>
                <div className={styles.rowMain}>
                  <p className={styles.rowName}>{r.product.name}</p>
                  <p className={styles.rowPrice}>{formatUsd(r.product.price_cents)}</p>
                </div>
                <div className={styles.qty}>
                  <button type="button" aria-label="Decrease" onClick={() => setQty(r.productId, r.quantity - 1)}>
                    <Minus size={15} />
                  </button>
                  <span>{r.quantity}</span>
                  <button type="button" aria-label="Increase" onClick={() => setQty(r.productId, r.quantity + 1)}>
                    <Plus size={15} />
                  </button>
                </div>
                <span className={styles.lineTotal}>{formatUsd(r.product.price_cents * r.quantity)}</span>
                <button type="button" className={styles.removeBtn} aria-label="Remove" onClick={() => remove(r.productId)}>
                  <X size={16} />
                </button>
              </li>
            );
          })}
        </ul>

        <aside className={styles.summary}>
          <div className={styles.sumRow}>
            <span>Subtotal</span>
            <span>{formatUsd(subtotalCents)}</span>
          </div>
          <div className={styles.sumRow}>
            <span>{freight && shipZip ? 'Hardware shipped to location' : 'Shipping'}</span>
            <span>{freight ? (shipZip ? 'Priced at checkout' : `${formatUsd(freight.amount_cents)}`) : 'Free'}</span>
          </div>
          {freight && <label className={styles.taxNote}>Ship-to ZIP (US), for the freight to your site{' '}
            <input value={zip} inputMode="numeric" maxLength={5} autoComplete="postal-code" placeholder="e.g. 90012" onChange={(e) => setZip(e.target.value.replace(/\D/g, ''))} /></label>}
          <p className={styles.taxNote}>Taxes calculated at checkout.</p>

          <button
            type="button"
            className="eviBtn eviBtnPrimary"
            style={{ width: '100%' }}
            disabled={busy}
            onClick={onCheckout}
          >
            {busy ? 'Starting checkout…' : 'Proceed to checkout'}
          </button>
          {/* Large orders (4+ chargers) still check out here; freight is a
              per-unit estimate and our team confirms logistics after the order.
              The "Talk to sales" link deep-links to the DC page's sales form
              (fixed: it used to land at the top of /commercial-dc). */}
          {bulk && (
            <div className={styles.bulkBox}>
              <p>
                Ordering {BULK_QUOTE_MIN_CHARGER_UNITS} or more chargers? Shipping shown is a
                per-unit estimate. After you place the order, our team confirms final freight and
                logistics within one business day.
              </p>
              <Link to="/commercial-dc#talk-to-sales" className={styles.bulkSalesLink}>
                Questions about a large order? Talk to sales
              </Link>
            </div>
          )}
          {error && <p className={styles.error}>{error}</p>}
          <p className={styles.disclaimer}>
            Shipping shown is an estimate. Final freight is confirmed when we process your order.
            Cross-border orders may carry customs, duties, or carrier surcharges, confirmed before
            your order ships.
          </p>
          <p className={styles.guestNote}>Guest checkout — email and shipping collected securely by Stripe. No account needed.</p>
        </aside>
      </div>
    </div>
  );
}
